import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Empty, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ajax from '../../utils/ajax';

// 后端约定:code !== 200 即失败
function checkBizCode(res) {
  if (res && typeof res === 'object' && 'code' in res) {
    return res.code === 200 || res.code === 0;
  }
  return true;
}

// 从 URL 安全提取 host(用于来源名称兜底显示)
function safeHost(url) {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return '';
  }
}

// 把 ISO/可解析日期格式化为 "2026年7月25日"
function formatFullDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

// 从接口单条数据抽取详情需要的字段
function pickDetailFields(item) {
  const rawDate =
    item.publishDate ||
    item.publish_date ||
    item.publishedAt ||
    item.date ||
    item.createdAt ||
    item.createTime ||
    '';

  const d = rawDate ? new Date(rawDate) : null;
  const valid = d && !Number.isNaN(d.getTime());
  const day = valid ? String(d.getDate()).padStart(2, '0') : '--';
  const month = valid
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    : '----';

  return {
    id: item.newsId,
    title: item.title || '',
    summary: item.summary || '',
    content: item.content || '',
    rawDate,
    day,
    month,
    sourceUrl: item.sourceUrl,
    sourceName:
      item.sourceName ||
      item.source ||
      (item.sourceUrl ? safeHost(item.sourceUrl) : ''),
  };
}

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 防止 Strict Mode / id 变化导致重复请求
  const requestedRef = useRef(false);

  useEffect(() => {
    // 重置 + 重新请求
    requestedRef.current = false;
    setItem(null);
    setError(null);
    setLoading(true);

    let cancelled = false;
    requestedRef.current = true;

    // 1) 优先调详情接口
    ajax
      .get(`/website/news/${id}`)
      .then((res) => {
        if (cancelled) return;
        if (checkBizCode(res) && res.data) {
          setItem(pickDetailFields(res.data));
        } else {
          // 详情接口业务失败 —— fallback 到列表接口
          return ajax.get('/website/news').then((listRes) => {
            if (cancelled) return;
            if (!checkBizCode(listRes)) {
              setError(listRes.msg || '加载失败,请稍后再试');
              return;
            }
            const list = (listRes.rows || []).map((it) => pickDetailFields(it));
            const hit = list.find((it) => String(it.id) === String(id));
            if (hit) {
              setItem(hit);
            } else {
              setError(`未找到 id 为 ${id} 的资讯,可能已被下线`);
            }
          });
        }
      })
      .catch(() => {
        // 详情接口 throw —— 也 fallback 到列表接口
        if (cancelled) return;
        ajax
          .get('/website/news')
          .then((listRes) => {
            if (cancelled) return;
            if (!checkBizCode(listRes)) {
              setError(listRes.msg || '加载失败,请稍后再试');
              return;
            }
            const list = (listRes.rows || []).map((it) => pickDetailFields(it));
            const hit = list.find((it) => String(it.id) === String(id));
            if (hit) {
              setItem(hit);
            } else {
              setError(`未找到 id 为 ${id} 的资讯,可能已被下线`);
            }
          })
          .catch((e) => {
            if (cancelled) return;
            setError(e.message || '资讯加载失败,请稍后再试');
          });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // 段落渲染:优先 content(支持 HTML 或 \n),再退到 summary,最后兜底
  const renderContent = () => {
    if (!item) return null;
    const text = (item.content || item.summary || '').trim();
    if (!text) {
      return (
        <>
          <p>
            围绕本则资讯所述事项,平台将持续同步政策动向与企业实操要点,便于财务、办税人员第一时间把握口径变化。
          </p>
          <p>
            如需进一步咨询,可在站内提交需求,顾问将在 24 小时内主动对接,提供与企业当前阶段匹配的解读与落地建议。
          </p>
        </>
      );
    }
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return (
        <div
          className="news-detail-content"
          dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    }
    return text.split(/\n+/).map((para, i) => <p key={i}>{para}</p>);
  };

  return (
    <div className="page-news news-detail-page">
      <section className="news-list-section">
        <div className="news-list-inner">
          {loading ? (
            // 骨架屏 —— 纯 div + shimmer 动画,避免 antd Skeleton.Node 在某些容器里渲染异常
            <div className="news-detail-skeleton" aria-busy="true" aria-live="polite">
              <div className="news-detail-skeleton-bar news-detail-skeleton-bar--title" />
              <div className="news-detail-skeleton-bar" />
              <div className="news-detail-skeleton-bar news-detail-skeleton-bar--short" />
              <div className="news-detail-skeleton-bar" />
              <div className="news-detail-skeleton-bar news-detail-skeleton-bar--medium" />
            </div>
          ) : error || !item ? (
            <Empty
              description={error || '该资讯不存在或已下线'}
              style={{ padding: '60px 0' }}
            >
              <Button type="primary" onClick={() => navigate('/news')}>
                返回资讯列表
              </Button>
            </Empty>
          ) : (
            <article className="news-detail">
              <Link to="/news" className="news-detail-back">
                <ArrowLeftOutlined />
                <span>返回资讯列表</span>
              </Link>

              <h1 className="news-detail-title">{item.title}</h1>
              {item.summary && item.summary !== item.content && (
                <p className="news-detail-subtitle">{item.summary}</p>
              )}

              <div className="news-detail-meta">
                <div className="news-detail-meta-item">
                  <span className="news-detail-meta-label">发布时间</span>
                  <span className="news-detail-meta-value">
                    {formatFullDate(item.rawDate) || `${item.month}-${item.day}`}
                  </span>
                </div>
                <div className="news-detail-meta-item">
                  <span className="news-detail-meta-label">信息来源</span>
                  <span className="news-detail-meta-value">
                    {item.sourceName || '官方渠道'}
                  </span>
                </div>
                <div className="news-detail-meta-item">
                  <span className="news-detail-meta-label">原文链接</span>
                  {item.sourceUrl ? (
                    <a
                      className="news-detail-meta-link"
                      href={item.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      查看原文
                    </a>
                  ) : (
                    <span className="news-detail-meta-value news-detail-meta-value--muted">
                      暂无
                    </span>
                  )}
                </div>
              </div>

              <hr className="news-detail-divider" />

              <div className="news-detail-content">{renderContent()}</div>
            </article>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;